<!-- CredentialSelectionModal.vue -->
<template>
  <div>
    <!-- Modal -->
    <div class="modal fade" id="credentialSelectionModal" tabindex="-1" aria-labelledby="credentialSelectionModalLabel" aria-hidden="true">
      <div class="modal-dialog modal-xl">
        <div class="modal-content">
          <div class="modal-header bg-warning text-dark">
            <h5 class="modal-title" id="credentialSelectionModalLabel">
              <i class="mdi mdi-shield-refresh me-2"></i>Select MPESA Source to Update
            </h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <!-- Info Alert -->
            <div class="alert alert-info" role="alert">
              <div class="d-flex">
                <i class="mdi mdi-information-outline font-size-20 me-2"></i>
                <div>
                  <h6 class="alert-heading mb-2">MPESA Credential Management</h6>
                  <ul class="mb-0">
                    <li><strong>Priority Update:</strong> Sources with expired or expiring credentials are highlighted</li>
                    <li><strong>Bulk Update:</strong> You can update credentials for any source, even if not expired</li>
                    <li><strong>Status Indicators:</strong> Color-coded badges show credential status</li>
                  </ul>
                </div>
              </div>
            </div>

            <!-- Quick Filters -->
            <div class="row mb-3">
              <div class="col-md-6">
                <div class="input-group">
                  <span class="input-group-text"><i class="bx bx-search"></i></span>
                  <input v-model="searchQuery" class="form-control" placeholder="Search MPESA sources..." type="text">
                </div>
              </div>
              <div class="col-md-6">
                <div class="btn-group w-100" role="group">
                  <button @click="filterStatus = 'all'" class="btn btn-sm" :class="filterStatus === 'all' ? 'btn-primary' : 'btn-outline-primary'">
                    All ({{filteredSources.length}})
                  </button>
                  <button @click="filterStatus = 'expired'" class="btn btn-sm" :class="filterStatus === 'expired' ? 'btn-danger' : 'btn-outline-danger'">
                    Expired ({{expiredCount}})
                  </button>
                  <button @click="filterStatus = 'expiring'" class="btn btn-sm" :class="filterStatus === 'expiring' ? 'btn-warning' : 'btn-outline-warning'">
                    Expiring Soon ({{expiringSoonCount}})
                  </button>
                  <button @click="filterStatus = 'active'" class="btn btn-sm" :class="filterStatus === 'active' ? 'btn-success' : 'btn-outline-success'">
                    Active ({{activeCount}})
                  </button>
                </div>
              </div>
            </div>

            <!-- Sources Table -->
            <div class="table-responsive" style="max-height: 400px; overflow-y: auto;">
              <table class="table table-hover table-sm">
                <thead class="table-light sticky-top">
                <tr>
                  <th width="5%">#</th>
                  <th width="40%">Account Name</th>
                  <th width="15%">Account Number</th>
                  <th width="15%">Last Updated</th>
                  <th width="15%" class="text-center">Status</th>
                  <th width="10%" class="text-center">Action</th>
                </tr>
                </thead>
                <tbody v-if="displayedSources.length > 0">
                <tr v-for="(source, index) in displayedSources"
                    :key="source.short_code"
                    :class="getRowClass(source)">
                  <td>{{index + 1}}</td>
                  <td>
                    <div class="d-flex align-items-center">
                      <div class="avatar-xs me-2">
                        <span class="avatar-title rounded-circle bg-soft-info text-info">
                          <i class="bx bx-mobile"></i>
                        </span>
                      </div>
                      <div>
                        <h6 class="mb-0">{{source.shortCodeName}}</h6>
                        <small class="text-muted">{{source.collectionTypeName}}</small>
                      </div>
                    </div>
                  </td>
                  <td><code>{{source.short_code}}</code></td>
                  <td>
                    <span v-if="source.date">
                      {{formatDate(source.date)}}
                      <br>
                      <small class="text-muted">({{getDaysAgo(source.date)}} ago)</small>
                    </span>
                    <span v-else class="text-muted">Never</span>
                  </td>
                  <td class="text-center">
                    <span class="badge" :class="getCredentialStatusBadge(source).class">
                      {{getCredentialStatusBadge(source).text}}
                    </span>
                  </td>
                  <td class="text-center">
                    <button @click="selectSource(source)"
                            class="btn btn-sm"
                            :class="getActionButtonClass(source)"
                            :title="getActionButtonTitle(source)">
                      <i class="mdi mdi-shield-refresh"></i>
                    </button>
                  </td>
                </tr>
                </tbody>
                <tbody v-else>
                <tr>
                  <td colspan="6" class="text-center py-4">
                    <p class="mb-0 text-muted">No MPESA sources found matching your criteria</p>
                  </td>
                </tr>
                </tbody>
              </table>
            </div>

            <!-- Summary Footer -->
            <div class="row mt-3 pt-3 border-top">
              <div class="col-md-8">
                <h6 class="mb-1">Credential Status Summary</h6>
                <div class="d-flex gap-3">
                  <small class="text-danger">
                    <i class="mdi mdi-circle me-1"></i>Expired: {{expiredCount}}
                  </small>
                  <small class="text-warning">
                    <i class="mdi mdi-circle me-1"></i>Expiring Soon: {{expiringSoonCount}}
                  </small>
                  <small class="text-success">
                    <i class="mdi mdi-circle me-1"></i>Active: {{activeCount}}
                  </small>
                  <small class="text-secondary">
                    <i class="mdi mdi-circle me-1"></i>Never Updated: {{neverUpdatedCount}}
                  </small>
                </div>
              </div>
              <div class="col-md-4 text-md-end">
                <small class="text-muted">
                  Total MPESA Sources: {{sources.length}}
                </small>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">
              Close
            </button>
            <button v-if="hasExpiredSources"
                    @click="updateAllExpired"
                    class="btn btn-danger">
              <i class="mdi mdi-shield-alert"></i> Update All Expired ({{expiredCount}})
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import moment from 'moment';

export default {
  name: "CredentialSelectionModal",
  props: {
    sources: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      searchQuery: '',
      filterStatus: 'all'
    }
  },
  computed: {
    filteredSources() {
      let filtered = this.sources;

      // Search filter
      if (this.searchQuery) {
        const search = this.searchQuery.toLowerCase();
        filtered = filtered.filter(source =>
            source.shortCodeName.toLowerCase().includes(search) ||
            source.short_code.toLowerCase().includes(search)
        );
      }

      // Status filter
      if (this.filterStatus !== 'all') {
        filtered = filtered.filter(source => {
          const status = this.getCredentialStatus(source);
          return status === this.filterStatus;
        });
      }

      return filtered;
    },

    displayedSources() {
      // Sort by priority: expired first, then expiring soon, then by name
      return [...this.filteredSources].sort((a, b) => {
        const aStatus = this.getCredentialStatus(a);
        const bStatus = this.getCredentialStatus(b);

        const statusPriority = {
          'expired': 1,
          'expiring': 2,
          'active': 3,
          'never': 4
        };

        if (statusPriority[aStatus] !== statusPriority[bStatus]) {
          return statusPriority[aStatus] - statusPriority[bStatus];
        }

        return a.shortCodeName.localeCompare(b.shortCodeName);
      });
    },

    expiredCount() {
      return this.sources.filter(source => this.getCredentialStatus(source) === 'expired').length;
    },

    expiringSoonCount() {
      return this.sources.filter(source => this.getCredentialStatus(source) === 'expiring').length;
    },

    activeCount() {
      return this.sources.filter(source => this.getCredentialStatus(source) === 'active').length;
    },

    neverUpdatedCount() {
      return this.sources.filter(source => this.getCredentialStatus(source) === 'never').length;
    },

    hasExpiredSources() {
      return this.expiredCount > 0;
    }
  },
  methods: {
    getCredentialStatus(source) {
      if (!source.date) {
        return 'never';
      }

      const lastUpdate = moment(source.date);
      const daysSinceUpdate = moment().diff(lastUpdate, 'days');
      const daysRemaining = 30 - daysSinceUpdate;

      if (daysRemaining <= 0) {
        return 'expired';
      } else if (daysRemaining <= 7) {
        return 'expiring';
      } else {
        return 'active';
      }
    },

    getCredentialStatusBadge(source) {
      const status = this.getCredentialStatus(source);

      switch (status) {
        case 'never':
          return { text: 'Never Updated', class: 'bg-secondary' };
        case 'expired':
          const lastUpdate = moment(source.date);
          const daysSinceUpdate = moment().diff(lastUpdate, 'days');
          const daysOverdue = daysSinceUpdate - 30;
          return {
            text: `Expired ${daysOverdue} days ago`,
            class: 'bg-danger'
          };
        case 'expiring':
          const daysLeft = 30 - moment().diff(moment(source.date), 'days');
          return {
            text: `${daysLeft} days left`,
            class: 'bg-warning'
          };
        case 'active':
          const activeDaysLeft = 30 - moment().diff(moment(source.date), 'days');
          return {
            text: `${activeDaysLeft} days left`,
            class: activeDaysLeft <= 15 ? 'bg-info' : 'bg-success'
          };
        default:
          return { text: 'Unknown', class: 'bg-secondary' };
      }
    },

    getRowClass(source) {
      const status = this.getCredentialStatus(source);
      switch (status) {
        case 'expired':
          return 'table-danger';
        case 'expiring':
          return 'table-warning';
        case 'never':
          return 'table-secondary';
        default:
          return '';
      }
    },

    getActionButtonClass(source) {
      const status = this.getCredentialStatus(source);
      switch (status) {
        case 'expired':
          return 'btn-danger';
        case 'expiring':
          return 'btn-warning';
        case 'never':
          return 'btn-secondary';
        default:
          return 'btn-outline-primary';
      }
    },

    getActionButtonTitle(source) {
      const status = this.getCredentialStatus(source);
      switch (status) {
        case 'expired':
          return 'Update expired credentials immediately';
        case 'expiring':
          return 'Update credentials expiring soon';
        case 'never':
          return 'Set up credentials for the first time';
        default:
          return 'Update active credentials';
      }
    },

    formatDate(date) {
      return moment(date).format('MMM D, YYYY');
    },

    getDaysAgo(date) {
      const days = moment().diff(moment(date), 'days');
      if (days === 0) return 'today';
      if (days === 1) return '1 day';
      return `${days} days`;
    },

    selectSource(source) {
      this.$emit('sourceSelected', source);
      this.hideModal();
    },

    updateAllExpired() {
      // For now, just select the first expired source
      // You could extend this to handle bulk updates
      const expiredSource = this.sources.find(source => this.getCredentialStatus(source) === 'expired');
      if (expiredSource) {
        this.selectSource(expiredSource);
      }
    },

    show() {
      this.searchQuery = '';
      this.filterStatus = 'all';

      // Show the modal using Bootstrap
      const modal = new bootstrap.Modal(document.getElementById('credentialSelectionModal'));
      modal.show();
    },

    hideModal() {
      const modal = bootstrap.Modal.getInstance(document.getElementById('credentialSelectionModal'));
      if (modal) {
        modal.hide();
      }
    }
  }
}
</script>

<style scoped>
.table-responsive::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

.table-responsive::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.table-responsive::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 4px;
}

.table-responsive::-webkit-scrollbar-thumb:hover {
  background: #555;
}

.bg-soft-info {
  background-color: rgba(50, 149, 239, 0.1);
}
</style>